import { Controller } from "@hotwired/stimulus";

// Filters a list of items by text content based on user input.
// Supports keyboard navigation (arrow keys + enter).
// Connects to data-controller="list-filter"
export default class extends Controller {
  static targets = ["input", "item", "empty"];
  static values = { autofocusDelay: { type: Number, default: -1 } };

  declare inputTarget: HTMLInputElement;
  declare hasInputTarget: boolean;
  declare itemTargets: HTMLElement[];
  declare emptyTarget: HTMLElement;
  declare hasEmptyTarget: boolean;
  declare autofocusDelayValue: number;

  activeIndex = -1;
  private focusTimeout?: ReturnType<typeof setTimeout>;
  private dropdownWasOpen?: boolean;

  // Opt-in delayed autofocus (autofocus-delay-value >= 0): focusing while a
  // drawer/modal is still animating scrolls the input into view and janks the
  // transition, so wait the given milliseconds first.
  connect() {
    if (this.autofocusDelayValue < 0 || !this.hasInputTarget) return;

    this.focusTimeout = setTimeout(() => {
      this.inputTarget.focus({ preventScroll: true });
      this.activeIndex = 0;
      this.updateHighlight();
    }, this.autofocusDelayValue);
  }

  disconnect() {
    if (this.focusTimeout) clearTimeout(this.focusTimeout);
  }

  focusInput() {
    if (!this.hasInputTarget) return;

    requestAnimationFrame(() => {
      this.inputTarget.focus();
      this.activeIndex = 0;
      this.updateHighlight();
    });
  }

  // A DaisyUI dropdown is open while focus is inside it, and a mouse press
  // focuses the trigger before its click lands, so the click alone cannot tell
  // an opening press from a closing one. The press records which it is.
  pressDropdown() {
    this.dropdownWasOpen = this.element.contains(document.activeElement);
  }

  // Closes an open dropdown by moving focus out of it; opens a closed one onto
  // the filter field, or onto the trigger when there is no field. A click with
  // no press before it (assistive technology) goes by the state as it stands.
  toggleDropdown(event: Event) {
    const wasOpen = this.dropdownWasOpen ?? this.element.contains(document.activeElement);
    this.dropdownWasOpen = undefined;

    if (wasOpen) {
      (document.activeElement as HTMLElement | null)?.blur();
    } else if (this.hasInputTarget) {
      this.focusInput();
    } else {
      (event.currentTarget as HTMLElement).focus();
    }
  }

  filter() {
    const query = this.inputTarget.value.toLowerCase().trim();

    this.itemTargets.forEach((item) => {
      const text = item.textContent?.toLowerCase() || "";
      item.style.display = text.includes(query) ? "" : "none";
    });

    const visible = this.visibleItems();
    this.toggleEmpty(visible.length === 0);
    this.activeIndex = visible.length > 0 ? 0 : -1;
    this.updateHighlight();
  }

  private toggleEmpty(isEmpty: boolean) {
    if (!this.hasEmptyTarget) return;
    this.emptyTarget.classList.toggle("hidden", !isEmpty);
  }

  reset() {
    if (!this.hasInputTarget) return;
    this.inputTarget.value = "";
    this.filter();
  }

  navigate(event: KeyboardEvent) {
    const visible = this.visibleItems();
    if (visible.length === 0) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.activeIndex = Math.min(this.activeIndex + 1, visible.length - 1);
        this.updateHighlight();
        break;
      case "ArrowUp":
        event.preventDefault();
        this.activeIndex = Math.max(this.activeIndex - 1, 0);
        this.updateHighlight();
        break;
      case "Enter":
        event.preventDefault();
        this.selectActive(visible);
        break;
    }
  }

  private visibleItems(): HTMLElement[] {
    return this.itemTargets.filter((item) => item.style.display !== "none");
  }

  private updateHighlight() {
    const visible = this.visibleItems();

    this.itemTargets.forEach((item) => item.classList.remove("list-filter-active"));

    if (this.activeIndex >= 0 && this.activeIndex < visible.length) {
      const active = visible[this.activeIndex];
      active.classList.add("list-filter-active");
      active.scrollIntoView({ block: "nearest" });
    }
  }

  // Safari, iOS included, does not move focus onto a button or link on
  // click, so a tap on a menu item blurs the trigger and the focus-within
  // dropdown hides before the click lands on anything. Cancelling the
  // mousedown keeps focus where it is; fields are left alone so the search
  // can still be typed into.
  holdFocus(event: Event) {
    if ((event.target as Element).closest("input, textarea, select")) return;

    event.preventDefault();
  }

  showLoading(event: Event) {
    const item = (event.currentTarget as HTMLElement).closest(
      "[data-list-filter-target='item']",
    ) as HTMLElement | null;
    if (item) this.showSpinner(item);
  }

  private selectActive(visible: HTMLElement[]) {
    const target =
      this.activeIndex >= 0 && this.activeIndex < visible.length
        ? visible[this.activeIndex]
        : visible[0];

    if (!target) return;

    const clickable =
      target.querySelector<HTMLElement>("button[type='submit']") ||
      target.querySelector<HTMLElement>("a") ||
      target.querySelector<HTMLElement>("button");

    clickable?.click();
  }

  private showSpinner(item: HTMLElement) {
    const button = item.querySelector("button[type='submit']") as HTMLButtonElement | null;
    if (!button || button.querySelector(".loading")) return;

    button.style.opacity = "0.5";
    button.style.pointerEvents = "none";

    const spinner = document.createElement("span");
    spinner.className = "loading loading-spinner loading-sm shrink-0";

    // Swap the row's trailing icon (a direct-child svg, e.g. the chevron) for
    // the spinner so it stays inside the flex row; appending to the button
    // itself would wrap it onto a new line under the card.
    const row = (button.firstElementChild as HTMLElement | null) ?? button;
    const trailingIcon = row.querySelector(":scope > svg");
    if (trailingIcon) {
      trailingIcon.replaceWith(spinner);
    } else {
      row.appendChild(spinner);
    }
  }
}
